import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  Mail, 
  Phone, 
  User, 
  Music, 
  Shield, 
  ArrowRight,
  RefreshCw,
  Lock,
  Unlock,
  AlertTriangle,
  Star,
  Heart,
  Zap,
  Crown,
  Sparkles,
  TrendingUp
} from 'lucide-react';

const VerificationDashboard = ({ user, userProfile, verificationStatus: propVerificationStatus, onComplete }) => {
  const [verificationStatus, setVerificationStatus] = useState(propVerificationStatus || {
    emailVerified: false,
    phoneVerified: false,
    onboardingCompleted: false,
    canReceiveRequests: false,
    email: user?.email || '',
    phoneNumber: null
  });
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phoneVerificationState, setPhoneVerificationState] = useState({
    step: 'enter_phone', // 'enter_phone', 'verify_code', 'verified'
    phoneNumber: '',
    code: '',
    isLoading: false,
    error: ''
  });

  const functions = getFunctions();

  // Update local state when props change
  useEffect(() => {
    if (propVerificationStatus) {
      setVerificationStatus(propVerificationStatus);
    }
  }, [propVerificationStatus]);

  useEffect(() => {
    if (!propVerificationStatus) {
      checkVerificationStatus();
    }
  }, [user, propVerificationStatus]);

  const checkVerificationStatus = async () => {
    if (!user) return;
    
    setIsRefreshing(true);
    try {
      const getUserVerificationStatus = httpsCallable(functions, 'getUserVerificationStatus');
      const result = await getUserVerificationStatus();
      
      if (result.data.success) {
        setVerificationStatus(result.data.verificationStatus);
        console.log('📋 Verification status loaded:', result.data.verificationStatus);
      } else {
        // Fallback status for new users
        setVerificationStatus(prev => ({
          ...prev,
          emailVerified: user.emailVerified || false
        }));
      }
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
      // Use email verification status from Firebase Auth as fallback
      setVerificationStatus(prev => ({
        ...prev,
        emailVerified: user.emailVerified || false
      }));
    } finally {
      setIsRefreshing(false);
    }
  };

  const refreshStatus = async () => {
    await checkVerificationStatus();
  };

  const sendPhoneVerification = async () => {
    if (!phoneVerificationState.phoneNumber.trim()) {
      setPhoneVerificationState(prev => ({ 
        ...prev, 
        error: 'Please enter your phone number first' 
      }));
      return;
    }

    if (!phoneVerificationState.phoneNumber.match(/^\+?[1-9]\d{1,14}$/)) {
      setPhoneVerificationState(prev => ({ 
        ...prev, 
        error: 'Please enter a valid phone number (e.g., +61412345678)' 
      }));
      return;
    }

    setPhoneVerificationState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const sendPhoneVerificationFunc = httpsCallable(functions, 'sendPhoneVerification');
      const result = await sendPhoneVerificationFunc({
        phoneNumber: phoneVerificationState.phoneNumber.trim()
      });

      if (result.data.success) {
        setPhoneVerificationState(prev => ({ 
          ...prev, 
          step: 'verify_code',
          isLoading: false,
          error: ''
        }));
        
        // Show dev code in console for testing
        if (result.data.devCode) {
          console.log('🔐 Verification code:', result.data.devCode);
          alert(`Development mode: Your verification code is ${result.data.devCode}`);
        }
      } else {
        throw new Error(result.data.message || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Phone verification error:', error);
      setPhoneVerificationState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: error.message || 'Failed to send verification code. Please try again.'
      }));
    }
  };

  const verifyPhoneCode = async () => {
    if (!phoneVerificationState.code.trim()) {
      setPhoneVerificationState(prev => ({ 
        ...prev, 
        error: 'Please enter the verification code' 
      }));
      return;
    }

    setPhoneVerificationState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const verifyPhoneCodeFunc = httpsCallable(functions, 'verifyPhoneCode');
      const result = await verifyPhoneCodeFunc({
        code: phoneVerificationState.code.trim()
      });

      if (result.data.success) {
        setPhoneVerificationState(prev => ({ 
          ...prev, 
          step: 'verified',
          isLoading: false,
          error: ''
        }));
        
        // Update verification status
        setVerificationStatus(prev => ({
          ...prev,
          phoneVerified: true,
          phoneNumber: phoneVerificationState.phoneNumber,
          canReceiveRequests: prev.emailVerified && prev.onboardingCompleted
        }));
        
        // Close modal after success
        setTimeout(() => {
          setShowPhoneVerification(false);
          refreshStatus();
        }, 2000);
      } else {
        throw new Error(result.data.message || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Phone code verification error:', error);
      setPhoneVerificationState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: error.message || 'Invalid verification code. Please try again.'
      }));
    }
  };

  const getVerificationSteps = () => {
    return [
      {
        id: 'email',
        title: 'Email Verification',
        description: 'Secure your account and recover access',
        encouragement: 'Just check your inbox! Takes 30 seconds ✨',
        benefit: 'Account security & password recovery',
        completed: verificationStatus.emailVerified,
        required: true,
        icon: Mail,
        action: 'Verify Email',
        status: verificationStatus.emailVerified ? 'complete' : 'pending'
      },
      {
        id: 'phone',
        title: 'Phone Verification',
        description: 'Get instant SMS alerts for every song request',
        encouragement: 'Never miss a paying customer! Quick 1-minute setup 📱',
        benefit: 'Instant alerts + 40% higher earnings',
        completed: verificationStatus.phoneVerified,
        required: false, // Optional but highly recommended
        icon: Phone,
        action: 'Add Phone Number',
        status: verificationStatus.phoneVerified ? 'complete' : 'recommended'
      }
    ];
  };

  const getOverallStatus = () => {
    const steps = getVerificationSteps();
    const completedSteps = steps.filter(step => step.completed).length;
    const totalSteps = steps.length;
    const emailVerified = verificationStatus.emailVerified;
    const phoneVerified = verificationStatus.phoneVerified;
    const onboardingCompleted = verificationStatus.onboardingCompleted;
    
    if (completedSteps === totalSteps && onboardingCompleted) {
      return {
        status: 'premium',
        title: '👑 Premium Account Activated!',
        message: 'You\'ve unlocked all features! Your account is optimized for maximum earnings.',
        color: '#7C3AED',
        bgColor: '#F3F4F6',
        icon: Crown
      };
    } else if (emailVerified && onboardingCompleted) {
      return {
        status: 'ready_plus',
        title: '🚀 Ready to Rock & Earn!',
        message: 'Your account is ready! Add phone verification to unlock instant SMS alerts and earn 40% more.',
        color: '#10B981',
        bgColor: '#ECFDF5',
        icon: Zap
      };
    } else if (onboardingCompleted) {
      return {
        status: 'almost_ready',
        title: '🌟 Almost Ready to Unlock Full Potential!',
        message: 'Quick verification steps will remove any restrictions and boost your earnings!',
        color: '#F59E0B',
        bgColor: '#FFFBEB',
        icon: Sparkles
      };
    } else {
      return {
        status: 'getting_started',
        title: '✨ Welcome! Let\'s Unlock Your Earning Potential',
        message: 'A few quick steps to remove restrictions and maximize your income!',
        color: '#7C3AED',
        bgColor: '#F3F4F6',
        icon: TrendingUp
      };
    }
  };

  const overallStatus = getOverallStatus();
  const steps = getVerificationSteps();
  const StatusIcon = overallStatus.icon;

  const canReceiveRequests = verificationStatus.emailVerified && verificationStatus.onboardingCompleted;
  const hasFullAccess = canReceiveRequests && verificationStatus.phoneVerified;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F9FAFB',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '24px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              margin: '0 0 8px 0',
              color: '#1F2937'
            }}>
              Unlock Your Full Potential
            </h1>
            <p style={{
              color: '#6B7280',
              margin: 0,
              fontSize: '16px'
            }}>
              Quick verification steps to remove restrictions and maximize earnings ✨
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={refreshStatus}
              disabled={isRefreshing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#F3F4F6',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                padding: '12px 16px',
                color: '#374151',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isRefreshing ? 'not-allowed' : 'pointer',
                opacity: isRefreshing ? 0.7 : 1
              }}
            >
              <RefreshCw 
                size={16} 
                style={{ 
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none' 
                }} 
              />
              Refresh Status
            </button>
            
            <button
              onClick={onComplete}
              style={{
                backgroundColor: '#7C3AED',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Music size={16} />
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px'
      }}>
        {/* Overall Status Card */}
        <div style={{
          backgroundColor: overallStatus.bgColor,
          border: `2px solid ${overallStatus.color}`,
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: overallStatus.color,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <StatusIcon size={32} style={{ color: 'white' }} />
            </div>
            <div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                margin: '0 0 8px 0',
                color: overallStatus.color
              }}>
                {overallStatus.title}
              </h2>
              <p style={{
                fontSize: '16px',
                margin: 0,
                color: '#374151',
                lineHeight: '1.5'
              }}>
                {overallStatus.message}
              </p>
            </div>
          </div>

          {/* Account Status Indicator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            backgroundColor: hasFullAccess 
              ? 'rgba(124, 58, 237, 0.1)' 
              : canReceiveRequests 
                ? 'rgba(16, 185, 129, 0.1)'
                : 'rgba(245, 158, 11, 0.1)',
            padding: '16px',
            borderRadius: '12px',
            border: `1px solid ${hasFullAccess ? '#7C3AED' : canReceiveRequests ? '#10B981' : '#F59E0B'}`
          }}>
            {hasFullAccess ? (
              <Crown size={20} style={{ color: '#7C3AED' }} />
            ) : canReceiveRequests ? (
              <Unlock size={20} style={{ color: '#10B981' }} />
            ) : (
              <Lock size={20} style={{ color: '#F59E0B' }} />
            )}
            <div>
              <span style={{
                fontWeight: '600',
                color: hasFullAccess ? '#5B21B6' : canReceiveRequests ? '#065F46' : '#92400E'
              }}>
                {hasFullAccess 
                  ? '👑 Premium Account - All Features Unlocked!' 
                  : canReceiveRequests
                    ? '🚀 Ready to Earn - Basic Features Active'
                    : '⏳ Getting Ready - Some Restrictions Apply'
                }
              </span>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#6B7280'
              }}>
                {hasFullAccess 
                  ? 'Maximum earning potential activated! You get instant SMS alerts and all premium features.'
                  : canReceiveRequests
                    ? 'You can receive requests! Add phone verification for instant SMS alerts and 40% higher earnings.'
                    : 'Complete verification to remove restrictions and start earning from your music!'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Overview */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '32px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0 0 24px 0',
            color: '#1F2937',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Sparkles size={24} style={{ color: '#7C3AED' }} />
            Why Complete Verification?
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            <div style={{
              backgroundColor: '#F0F9FF',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #0EA5E9'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <TrendingUp size={20} style={{ color: '#0EA5E9' }} />
                <h4 style={{ margin: 0, color: '#0C4A6E', fontWeight: '600' }}>40% Higher Earnings</h4>
              </div>
              <p style={{ margin: 0, color: '#0369A1', fontSize: '14px' }}>
                Verified musicians earn significantly more because fans trust them more.
              </p>
            </div>
            
            <div style={{
              backgroundColor: '#F0FDF4',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #22C55E'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <Zap size={20} style={{ color: '#22C55E' }} />
                <h4 style={{ margin: 0, color: '#14532D', fontWeight: '600' }}>Instant Notifications</h4>
              </div>
              <p style={{ margin: 0, color: '#166534', fontSize: '14px' }}>
                Get SMS alerts the moment someone requests a song - never miss earnings!
              </p>
            </div>
            
            <div style={{
              backgroundColor: '#FEF7FF',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #A855F7'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <Crown size={20} style={{ color: '#A855F7' }} />
                <h4 style={{ margin: 0, color: '#581C87', fontWeight: '600' }}>Premium Features</h4>
              </div>
              <p style={{ margin: 0, color: '#7C2D92', fontSize: '14px' }}>
                Unlock advanced analytics, priority support, and exclusive features.
              </p>
            </div>
          </div>
        </div>

        {/* Verification Steps */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            return (
              <div
                key={step.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '24px',
                  border: step.completed 
                    ? '2px solid #10B981' 
                    : step.required 
                      ? '2px solid #F59E0B'
                      : '2px solid #7C3AED',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  position: 'relative'
                }}
              >
                {/* Step Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  width: '32px',
                  height: '32px',
                  backgroundColor: step.completed ? '#10B981' : step.required ? '#F59E0B' : '#7C3AED',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {step.completed ? '✓' : step.required ? '!' : '★'}
                </div>

                {/* Recommended Badge */}
                {!step.required && !step.completed && (
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    backgroundColor: '#7C3AED',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Recommended
                  </div>
                )}

                {/* Step Content */}
                <div style={{ marginBottom: '20px', marginTop: !step.required ? '24px' : '0' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: step.completed ? '#ECFDF5' : '#F3F4F6',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <StepIcon 
                        size={24} 
                        style={{ 
                          color: step.completed ? '#10B981' : '#6B7280' 
                        }} 
                      />
                    </div>
                    <div>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        margin: '0 0 4px 0',
                        color: '#1F2937'
                      }}>
                        {step.title}
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        margin: 0,
                        color: '#6B7280',
                        lineHeight: '1.4'
                      }}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Benefit highlight */}
                  <div style={{
                    backgroundColor: step.completed ? '#ECFDF5' : '#EFF6FF',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }}>
                    <p style={{
                      fontSize: '14px',
                      color: step.completed ? '#059669' : '#1D4ED8',
                      fontWeight: '600',
                      margin: 0
                    }}>
                      💎 {step.benefit}
                    </p>
                  </div>
                  
                  {/* Encouragement message */}
                  <p style={{
                    fontSize: '14px',
                    color: step.completed ? '#059669' : '#7C3AED',
                    fontWeight: '500',
                    margin: '8px 0 0 0',
                    fontStyle: 'italic'
                  }}>
                    {step.completed ? '✅ Completed! You\'re earning more!' : step.encouragement}
                  </p>
                </div>

                {/* Step Action */}
                {step.completed ? (
                  <div style={{
                    backgroundColor: '#ECFDF5',
                    border: '1px solid #10B981',
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <CheckCircle size={16} style={{ color: '#10B981' }} />
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#065F46'
                    }}>
                      Verified & Active
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      if (step.id === 'phone') {
                        setShowPhoneVerification(true);
                      } else if (step.id === 'email') {
                        // Handle email verification
                        console.log('Email verification clicked');
                      }
                    }}
                    style={{
                      width: '100%',
                      backgroundColor: step.required ? '#F59E0B' : '#7C3AED',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = step.required ? '#D97706' : '#6D28D9'}
                    onMouseOut={(e) => e.target.style.backgroundColor = step.required ? '#F59E0B' : '#7C3AED'}
                  >
                    {step.action}
                    <ArrowRight size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div style={{
          backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          padding: '32px',
          textAlign: 'center',
          color: 'white',
          marginBottom: '32px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚀</div>
          <h3 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0 0 12px 0'
          }}>
            Ready to Start Earning More?
          </h3>
          <p style={{
            margin: '0 0 24px 0',
            fontSize: '16px',
            lineHeight: '1.5',
            opacity: 0.9
          }}>
            Each verification step takes less than 2 minutes and removes restrictions while boosting your earning potential. 
            Most musicians see results immediately!
          </p>
          
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {!verificationStatus.phoneVerified && (
              <button
                onClick={() => setShowPhoneVerification(true)}
                style={{
                  backgroundColor: 'white',
                  color: '#667eea',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Phone size={20} />
                Verify Phone (2 min)
              </button>
            )}
            
            <button
              onClick={onComplete}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: '2px solid white',
                borderRadius: '12px',
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Music size={20} />
              Continue to Dashboard
            </button>
          </div>
          
          <p style={{
            margin: '16px 0 0 0',
            fontSize: '14px',
            opacity: 0.8
          }}>
            💡 You can always complete verification later in your dashboard settings
          </p>
        </div>
      </div>

      {/* Phone Verification Modal - Same as before */}
      {showPhoneVerification && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                margin: 0,
                color: '#1F2937'
              }}>
                📱 Verify Phone for Instant Alerts
              </h3>
              <button
                onClick={() => setShowPhoneVerification(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6B7280',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{
              backgroundColor: '#EFF6FF',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid #DBEAFE'
            }}>
              <h4 style={{
                margin: '0 0 12px 0',
                color: '#1E40AF',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                🚀 Boost Your Earnings by 40%!
              </h4>
              <ul style={{
                margin: '0',
                paddingLeft: '20px',
                color: '#374151',
                lineHeight: '1.6'
              }}>
                <li>Get instant SMS when fans request songs</li>
                <li>Never miss a paying customer again</li>
                <li>Verified musicians earn 40% more on average</li>
                <li>Takes just 60 seconds to set up</li>
              </ul>
            </div>

            {phoneVerificationState.step === 'enter_phone' && (
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phoneVerificationState.phoneNumber}
                    onChange={(e) => setPhoneVerificationState(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    placeholder="+61412345678 (include country code)"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '16px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                  />
                </div>

                {phoneVerificationState.error && (
                  <div style={{
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px'
                  }}>
                    <p style={{ color: '#DC2626', margin: 0, fontSize: '14px' }}>
                      {phoneVerificationState.error}
                    </p>
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  flexDirection: 'column'
                }}>
                  <button
                    onClick={sendPhoneVerification}
                    disabled={phoneVerificationState.isLoading}
                    style={{
                      backgroundColor: phoneVerificationState.isLoading ? '#9CA3AF' : '#7C3AED',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '14px 20px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: phoneVerificationState.isLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Phone size={20} />
                    {phoneVerificationState.isLoading ? 'Sending...' : 'Send Code & Unlock 40% More Earnings'}
                  </button>
                  
                  <button
                    onClick={() => setShowPhoneVerification(false)}
                    style={{
                      backgroundColor: '#F3F4F6',
                      color: '#374151',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      padding: '14px 20px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
            )}

            {phoneVerificationState.step === 'verify_code' && (
              <div>
                <p style={{ 
                  color: '#374151', 
                  fontSize: '16px', 
                  margin: '0 0 24px 0',
                  textAlign: 'center'
                }}>
                  We sent a code to <strong>{phoneVerificationState.phoneNumber}</strong>
                </p>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={phoneVerificationState.code}
                    onChange={(e) => setPhoneVerificationState(prev => ({ ...prev, code: e.target.value }))}
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '18px',
                      textAlign: 'center',
                      letterSpacing: '2px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                  />
                </div>

                {phoneVerificationState.error && (
                  <div style={{
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px'
                  }}>
                    <p style={{ color: '#DC2626', margin: 0, fontSize: '14px' }}>
                      {phoneVerificationState.error}
                    </p>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={verifyPhoneCode}
                    disabled={phoneVerificationState.isLoading}
                    style={{
                      flex: 1,
                      backgroundColor: phoneVerificationState.isLoading ? '#9CA3AF' : '#10B981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '14px 20px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: phoneVerificationState.isLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {phoneVerificationState.isLoading ? 'Activating...' : 'Activate Premium Features'}
                  </button>
                  
                  <button
                    onClick={() => setPhoneVerificationState({ step: 'enter_phone', phoneNumber: '', code: '', isLoading: false, error: '' })}
                    style={{
                      backgroundColor: '#F3F4F6',
                      color: '#374151',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      padding: '14px 16px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {phoneVerificationState.step === 'verified' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
                <h3 style={{ 
                  color: '#059669', 
                  fontSize: '24px', 
                  fontWeight: 'bold', 
                  margin: '0 0 16px 0'
                }}>
                  Premium Features Activated!
                </h3>
                <p style={{ 
                  color: '#047857', 
                  fontSize: '16px', 
                  margin: 0
                }}>
                  You'll now earn 40% more with instant SMS notifications for every song request!
                </p>
              </div>
            )}

            <div style={{
              marginTop: '20px',
              fontSize: '12px',
              color: '#6B7280',
              textAlign: 'center',
              lineHeight: '1.4'
            }}>
              🔒 Your phone number is securely stored and never shared.
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VerificationDashboard;